import { ACCOUNT_TYPE, fetchUserData, selectUserData, updateValue } from '../accountSlice'
import { sortSavedMethods, type SavedPaymentMethod } from './types'
import { useReduxDispatch, useReduxSelector } from 'store'
import PaymentMethodBadges from './PaymentMethodBadges'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import BackBtn from 'components/buttons/BackBtn'
import { useHistory } from 'react-router-dom'
import { toLongDate, toTime } from 'utils/date'
import { DELETE, PATCH } from 'rest/request'
import Switch from 'components/inputs/Switch'
import Popup from 'components/popups/Popup'
import Loading from 'components/Loading'
import React, { useState } from 'react'
import * as urls from 'rest/urls'

const type = ACCOUNT_TYPE.cards

interface PaymentCardFormProps {
  id?: string
}

const PaymentCardItem: React.FC<PaymentCardFormProps> = ({ id }) => {
  const { data } = useReduxSelector(state => selectUserData(state, type))
  const list: SavedPaymentMethod[] = Array.isArray(data) ? data : []
  const method = list.find(m => m.id === id)

  const [busy, setBusy] = useState<'default' | 'remove' | null>(null)
  const [confirmRemove, setConfirmRemove] = useState(false)

  const dispatch = useReduxDispatch()
  const { goBack } = useHistory()
  const { t, lang } = useTranslation('account')

  const dateTime = (value?: string | null) => value ? `${toLongDate(value, lang)}, ${toTime(value, lang)}` : null

  const setDefault = async () => {
    if (!method || method.isDefault || busy) return
    setBusy('default')
    const previous = list
    const next = list.map(m => m.id === method.id
      ? { ...m, isDefault: true }
      : m.companyName === method.companyName ? { ...m, isDefault: false } : m)
    dispatch(updateValue({ type, name: 'data', value: sortSavedMethods(next) }))
    try {
      await PATCH(urls.defaultPaymentMethod(method.id), { credentials: true })
      dispatch(fetchUserData(type))
    } catch (error) {
      dispatch(updateValue({ type, name: 'data', value: previous }))
    } finally {
      setBusy(null)
    }
  }

  const removeMethod = async () => {
    if (!method || busy) return
    setBusy('remove')
    try {
      await DELETE(`${urls.removePaymentMethods}${method.id}`)
      dispatch(updateValue({ type, name: 'data', value: list.filter(m => m.id !== method.id) }))
      goBack()
      dispatch(fetchUserData(type))
    } catch (error) {
      setBusy(null)
      setConfirmRemove(false)
    }
  }

  const info: [string, React.ReactNode][] = method ? ([
    ['methodType', t(`savedMethodKinds.${method.kind}`)],
    ['cardValidityDate', method.cardValidityDate],
    ['cardIssuer', method.cardIssuer],
    ['paymentOperator', method.operator],
    ['company', method.companyName],
    ['usedForAutomaticPayments', t(method.isMandate ? 'yes' : 'no')],
    ['consentDate', dateTime(method.consentAt)],
    ['lastCharge', dateTime(method.lastChargeAt)],
  ] as [string, React.ReactNode][]).filter(([, value]) => value !== null && value !== undefined && value !== '') : []

  const inactive = method && method.status !== 'active'

  return (
    <>
      <Popup>
        <div className='account__card'>
          <div className='popup__form form summary pmDetails'>
            <div className='popup__title'>
              <div>{method?.displayName || t('paymentMethod')}</div>
            </div>

            {method
              ? <div className='account__list list'>
                <div>
                  <div className='list__header element__title'>{t('status')}</div>
                  <div className='list__element --big'>
                    <div>
                      <div className='element__title pmTitle'>
                        <span>{t(`savedMethodStatuses.${method.status}`)}</span>
                        <PaymentMethodBadges method={method} />
                      </div>
                      {inactive && <div className='element__desc pmStatusNote'>{t(`savedMethodStatusDesc.${method.status}`)}</div>}
                      {!!method.failedAttempts && <div className='element__desc'>{t('failedAttempts')}: <span>{method.failedAttempts}</span></div>}
                      {method.lastError && <div className='element__desc'>{t('lastError')}: <span>{method.lastError}</span></div>}
                    </div>
                  </div>
                </div>

                <div>
                  <div className='list__header element__title'>{t('information')}</div>
                  <div className='list__element --big'>
                    <div>
                      {info.map(([key, value]) => (
                        <div key={key} className='element__desc'>{t(key)}: <span>{value}</span></div>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <div className='list__header element__title'>{t('settings')}</div>
                  <div className='list__element --big'>
                    <div className='pmSwitchRow'>
                      <div>
                        <div className='element__title'>{t('setAsDefault')}</div>
                        <div className='element__desc'>{t('defaultMethodDesc')}</div>
                      </div>
                      <Switch
                        name={`defaultMethod_${method.id}`}
                        checked={method.isDefault}
                        disabled={method.isDefault || method.status !== 'active' || !!busy}
                        changeHandler={setDefault} />
                    </div>
                  </div>
                </div>

                <div className='pmRemove'>
                  <BackBtn
                    text={t('removePaymentMethod')}
                    action={() => setConfirmRemove(true)} />
                </div>
              </div>
              : <Loading />}
          </div>
        </div>
      </Popup>

      {confirmRemove && <Popup
        close={() => {
          setConfirmRemove(false)
        }}>
        <div className='pmConfirm'>
          <div className='popup__title'>
            <div>{t('caution')}</div>
          </div>
          <p>{t('removePaymentMethodConfirm')}</p>
          <ArrowBtn
            enlarged
            disabled={!!busy}
            text={t('confirmRemove')}
            isLoading={busy === 'remove'}
            action={removeMethod} />
          <BackBtn
            text={t('cancel')}
            action={() => !busy && setConfirmRemove(false)} />
        </div>
      </Popup>}
    </>
  )
}

export default PaymentCardItem
